import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import Wordmark from "@/components/Wordmark";

export default function LoginPage() {
  const { user, login, refreshMe } = useAuth();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user === null) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#FAF9F7]">
        <p className="font-sans text-sm text-[#7A7571]">Loading…</p>
      </div>
    );
  }
  if (user && user.id) {
    return <Navigate to={nextPath || "/"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    if (!res.ok) {
      setSubmitting(false);
      setError(res.error || "Login failed");
      return;
    }
    const confirmed = await refreshMe();
    setSubmitting(false);
    if (!confirmed) {
      setError(
        "Signed in, but your browser dropped the session cookie. " +
          "This usually means a stale cache — hard-reload " +
          "(Cmd/Ctrl-Shift-R) and try again."
      );
      // eslint-disable-next-line no-console
      console.error(
        "[onebrain] login OK but /auth/me 401 — likely stale bundle or cookie blocker.",
        { docCookieEmpty: !document.cookie }
      );
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6 py-10 bg-[#F3F1EC]"
      data-testid="login-page"
    >
      <div className="absolute top-8 left-8 font-serif text-xl tracking-tight text-[#2C2A29] select-none">
        <Link to="/" className="hover:opacity-80 transition-opacity">
          <Wordmark size="md" testid="login-header-wordmark" />
        </Link>
      </div>

      <div className="w-full max-w-md bg-[#FAF9F7] border border-[#DCD7CE] rounded-2xl shadow-sm px-8 py-10 sm:px-10 sm:py-12">
        <p className="font-sans text-xs uppercase tracking-[0.22em] text-[#7A7571] mb-3">
          Shared workspace
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none text-[#2C2A29] mb-3">
          Welcome back.
        </h1>
        <p className="font-sans text-sm text-[#7A7571] mb-8 leading-relaxed">
          Sign in to step into the conversation your workspace has been having
          with Claude.
        </p>

        <form
          onSubmit={handleSubmit}
          className="space-y-5"
          data-testid="login-form"
        >
          <div className="space-y-2">
            <label
              htmlFor="email"
              className="block font-sans text-xs uppercase tracking-widest text-[#7A7571] font-semibold"
            >
              Email
            </label>
            <input
              id="email"
              data-testid="login-email-input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@somewhere.com"
              className="w-full px-4 py-3 rounded-lg bg-white border border-[#DCD7CE] font-sans text-base text-[#2C2A29] placeholder:text-[#A5A29E] focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30 focus:border-[#D96C4A]/40 transition"
              required
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="password"
              className="block font-sans text-xs uppercase tracking-widest text-[#7A7571] font-semibold"
            >
              Password
            </label>
            <input
              id="password"
              data-testid="login-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-3 rounded-lg bg-white border border-[#DCD7CE] font-sans text-base text-[#2C2A29] placeholder:text-[#A5A29E] focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30 focus:border-[#D96C4A]/40 transition"
              required
            />
          </div>

          {error ? (
            <div
              data-testid="login-error"
              className="rounded-lg border border-[#D96C4A]/30 bg-[#D96C4A]/10 px-4 py-3 font-sans text-sm text-[#8C3A1F]"
            >
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={submitting}
            className="w-full px-4 py-3 rounded-lg bg-[#2C2A29] text-white font-sans text-sm font-medium tracking-wide hover:bg-[#1A1918] disabled:opacity-60 disabled:cursor-not-allowed transition"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-center">
            <Link
              to="/forgot-password"
              data-testid="login-forgot-password-link"
              className="font-sans text-xs text-[#7A7571] hover:text-[#1E7BFF] underline underline-offset-2 transition-colors"
            >
              Forgot password?
            </Link>
          </div>
        </form>

        <p className="mt-8 font-sans text-xs text-[#7A7571] leading-relaxed">
          onebrain keeps you signed in until you log out — you&apos;ll rarely need to log in
          again from this device.
        </p>
        <p className="mt-3 font-sans text-sm text-[#7A7571]">
          New to onebrain?{" "}
          <Link
            to="/signup"
            data-testid="login-signup-link"
            className="text-[#2C2A29] underline underline-offset-2 hover:text-[#D96C4A] transition-colors"
          >
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
